import { useState } from 'react'
import type { JSX } from 'react'

import { useSplitEnabled } from '@buffer-mono/features'
import { Flex, Skeleton } from '@buffer-mono/popcorn'

import { PostCardProvider, PostCard_Post } from '~publish/components/PostCard'
import { getAnalyzeUrl } from '~publish/components/PostCard/helpers'
import { PostCardAttachments } from '~publish/components/PostCard/PostCardAttachments'
import {
  useMetadataTitle,
  usePostData,
} from '~publish/components/PostCard/PostCardContext'
import { PostCardErrorNotice } from '~publish/components/PostCard/PostCardErrorNotice'
import { PostMediaBentoBoxPreview } from '~publish/components/PostCard/PostMedia'
import { PostCardAddons } from '~publish/components/PostCardAddons'
import { PostCardContent } from '~publish/components/PostCardContent'
import { PostCardContent_Annotation } from '~publish/components/PostCardContent/PostCardContent'
import { PostCardFooter } from '~publish/components/PostCardFooter'
import { PostCardHeader } from '~publish/components/PostCardHeader'
import { PostCardTags } from '~publish/components/PostCardTags'
import { PostCardView } from '~publish/components/PostCardView'
import {
  InsightsIconButton,
  MetricsRefresh,
  MoreAnalyticsIconButton,
  PostMetricsScroller,
  PostMetricsWrapper,
} from '~publish/components/PostDetails/PostMetrics'
import {
  PostGroupDialog,
  POST_GROUPS_UI_FLAG,
  usePostDetailsDialog,
} from '~publish/components/PostDialogs'
import { readFragment } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'
import { useCanShowAnalyzeFallback } from '~publish/hooks/useAnalyzeSunsetEnabled'
import { useGetEngagementUrl } from '~publish/hooks/useGetEngagementUrl'
import { isChannelSupportedByInsights } from '~publish/pages/Insights/utils'

import { CalendarPostCardHeader } from './CalendarPostCardHeader'
import { CalendarPostCard_Post } from '../fragments/CalendarPostCard_Post'

import styles from './CalendarPostCard.module.css'

type CalendarPostCardProps = {
  post?: FragmentOf<typeof PostCard_Post>
  partialData: FragmentOf<typeof CalendarPostCard_Post>
}

const TagsAndAddons = (): JSX.Element => (
  <Flex direction="column" gap="xs" className={styles.tagsAddonsContainer}>
    <PostCardAddons />
    <PostCardTags maxVisibleTags={4} />
  </Flex>
)

const Metrics = (): JSX.Element | null => {
  const { metrics: metricsData, channel, id: postId } = usePostData()
  const metrics = metricsData ?? []

  const canShowAnalyzeFallback = useCanShowAnalyzeFallback()

  const engageUrl = useGetEngagementUrl(channel, postId)
  const analyzeUrl = getAnalyzeUrl(channel)

  const shouldShowMoreAnalyticsCTA =
    canShowAnalyzeFallback &&
    (channel.service === 'linkedin' ? channel.type === 'page' : true)
  const shouldShowInsightsCTA = isChannelSupportedByInsights(
    channel.service,
    channel.type,
  )

  return (
    <PostMetricsWrapper className={styles.metricsWrapper}>
      <PostMetricsScroller metrics={metrics} engageUrl={engageUrl} />
      {shouldShowInsightsCTA ? (
        <InsightsIconButton insightsUrl={`/insights/${channel.id}`} />
      ) : (
        shouldShowMoreAnalyticsCTA &&
        analyzeUrl && <MoreAnalyticsIconButton analyzeUrl={analyzeUrl} />
      )}
      <MetricsRefresh />
    </PostMetricsWrapper>
  )
}

export const CalendarPostCard = ({
  post,
  partialData,
}: CalendarPostCardProps): JSX.Element => {
  const postData = readFragment(PostCard_Post, post)
  const partialPostData = readFragment(CalendarPostCard_Post, partialData)
  const postId = postData?.id ?? partialPostData.id
  // Trust the fully-loaded post first; only fall back to the partial fragment
  // while the full query is still in flight. A loaded post with `postGroup:
  // null` should NOT inherit the partial's auto-mocked group in tests.
  const postGroupId = postData
    ? postData.postGroup?.id
    : partialPostData.postGroup?.id
  const { openDetailsDialog } = usePostDetailsDialog({ postId })
  const title = useMetadataTitle(postData?.metadata ?? partialPostData.metadata)
  const type = postData?.metadata?.type ?? partialPostData.metadata?.type
  const annotations = readFragment(
    PostCardContent_Annotation,
    postData?.metadata?.annotations ?? [],
  )

  const { isReady, isEnabled } = useSplitEnabled(POST_GROUPS_UI_FLAG)
  const useGroupDialog = isReady && isEnabled && Boolean(postGroupId)
  const [groupDialogOpen, setGroupDialogOpen] = useState(false)

  // Grouped posts open the in-place group dialog so siblings stay one click
  // away; ungrouped posts keep the route-mounted Post Details dialog, which
  // honors the `expandText` query param to land on the fully expanded text
  // the user clicked to read.
  const handleSeeMoreClick = (): void => {
    if (useGroupDialog) {
      setGroupDialogOpen(true)
      return
    }
    openDetailsDialog({
      cta: 'publish-calendar-postCardText-seeMore-1',
      expandText: 'true',
    })
  }
  return (
    <PostCardProvider
      post={
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        post ?? (partialData as unknown as FragmentOf<typeof PostCard_Post>)
      }
    >
      <PostCardView
        className={styles.cardContainer}
        banner={<PostCardErrorNotice clampErrorMessage />}
        header={<CalendarPostCardHeader />}
        content={
          <>
            <PostCardHeader type={type} condensed />
            <PostCardContent
              title={title}
              text={postData?.text ?? partialPostData.text}
              annotations={annotations}
              onSeeMoreClick={handleSeeMoreClick}
            />
            <PostCardAttachments condensed />
          </>
        }
        media={
          <PostMediaBentoBoxPreview
            assets={postData?.assets ?? partialPostData.assets}
            className={styles.bentoBox}
          />
        }
        meta={
          postData ? (
            <TagsAndAddons />
          ) : (
            <div className={styles.tagsAddonsContainerSkeleton}>
              <Skeleton height={24} width={24} />
            </div>
          )
        }
        metrics={postData ? <Metrics /> : null}
        footer={
          postData ? (
            <PostCardFooter condensed />
          ) : (
            <PostCardFooter.Skeleton
              isSent={partialPostData.status === 'sent'}
            />
          )
        }
      />
      {useGroupDialog && (
        <PostGroupDialog
          postId={postId}
          postGroupId={postGroupId}
          open={groupDialogOpen}
          onOpenChange={setGroupDialogOpen}
        />
      )}
    </PostCardProvider>
  )
}
